<template>
    <div class="matchInfo_card">
        <div class="participant_container">
            <div
                class="participant"
            >
                <div class="participant_logo">
                    <picture v-if="homeTeamImage">
                        <source
                            v-if="homeTeamImage.webp"
                            v-bind:srcset="homeTeamImage.webp"
                            type="image/webp"
                        />
                        <img
                            v-bind:src="homeTeamImage.origin"
                            alt=""
                            aria-hidden="true"
                        />
                    </picture>
                </div>
                <div
                    class="participant_name"
                    v-bind:class="{ _favorite: event.homeTeam.isFavorite }"
                >
                    {{ homeName }}
                </div>
                <div v-if="event.isLive" class="badge">
                    <FoulCard v-bind:count="defaultRedCardCountForDisplay" />
                    <div class="badge_text">
                        {{ homeTeamRedCard }}
                    </div>
                </div>
            </div>
        </div>
        <div class="centerInfo">
            <ul class="infoList">
                <template v-if="event.isLive">
                    <li class="statusTag">
                        <span class="statusDot" />{{ displayScore?.periodText }}
                        <span
                            v-if="isShowInjuryTime(event)"
                        >
                            +{{ event.extraInfo?.injuryTime }}'
                        </span>
                    </li>
                    <li class="result">
                        <span class="value">{{ displayScore?.score?.home ?? 0 }}</span>
                        <span class="separator">:</span>
                        <span class="value">{{ displayScore?.score?.away ?? 0 }}</span>
                    </li>
                </template>
                <template v-else>
                    <li class="schedule">
                        <span class="date">{{ dateDisplay }}</span>
                        <span class="time">{{ timeDisplay }}</span>
                    </li>
                    <li v-if="isShowCountdown" class="timer">
                        <span>{{ countdownDisplay.hours }}</span>
                        <span class="separator">:</span>
                        <span>{{ countdownDisplay.minutes }}</span>
                        <span class="separator">:</span>
                        <span>{{ countdownDisplay.seconds }}</span>
                    </li>
                    <li v-else class="timer">
                        <span>--</span>
                        <span class="separator">:</span>
                        <span>--</span>
                        <span class="separator">:</span>
                        <span>--</span>
                    </li>
                </template>
                <VButton
                    v-if="isStatisticsAvailable"
                    v-gtm:label="'Statistic'"
                    v-gtm:@click
                    v-on:click="openStatistics"
                >
                    <SboIcon name="system:chart" size="24" />
                </VButton>
            </ul>
        </div>
        <div class="participant_container">
            <div
                class="participant"
            >
                <div class="participant_logo">
                    <picture v-if="awayTeamImage">
                        <source
                            v-if="awayTeamImage.webp"
                            v-bind:srcset="awayTeamImage.webp"
                            type="image/webp"
                        />
                        <img
                            v-bind:src="awayTeamImage.origin"
                            alt=""
                            aria-hidden="true"
                        />
                    </picture>
                </div>
                <div
                    class="participant_name"
                    v-bind:class="{ _favorite: event.awayTeam.isFavorite }"
                >
                    {{ awayName }}
                </div>
                <div v-if="event.isLive" class="badge">
                    <FoulCard v-bind:count="defaultRedCardCountForDisplay" />
                    <div class="badge_text">
                        {{ awayTeamRedCard }}
                    </div>
                </div>
            </div>
        </div>
        <div class="matchInfo_footballPlayer matchInfo_footballPlayer_home" aria-hidden="true">
            <picture v-if="homePlayerImage">
                <source
                    v-if="homePlayerImage.webp"
                    v-bind:srcset="homePlayerImage.webp"
                    type="image/webp"
                />
                <img
                    v-bind:src="homePlayerImage.origin"
                    alt=""
                />
            </picture>
        </div>
        <div class="matchInfo_footballPlayer matchInfo_footballPlayer_away" aria-hidden="true">
            <picture v-if="awayPlayerImage">
                <source
                    v-if="awayPlayerImage.webp"
                    v-bind:srcset="awayPlayerImage.webp"
                    type="image/webp"
                />
                <img
                    v-bind:src="awayPlayerImage.origin"
                    alt=""
                />
            </picture>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { computed, ref, toRefs } from 'vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import VButton from '@/components/common/button/VButton.vue';
    import FoulCard from '@/components/common/foulCard/FoulCard.vue';
    import { isShowInjuryTime } from '@/components/oddsDisplay/matchDisplay/useMatchDisplay';
    import { getTournamentCenterPlayerImage, getTournamentCenterTeamImage } from '@/components/tournamentCenter/tournamentCenterHelper';
    import { useEventCountdown } from '@/composable/useEventCountDown';
    import { useLiveScoreDisplay } from '@/composable/useLiveScoreDisplay';
    import { useLiveScoreRestriction } from '@/composable/useLiveScoreRestriction';
    import { useStatistics } from '@/composable/useStatistics';
    import { formatDate } from '@/core/lib/date';
    import { getTranslated } from '@/core/lib/translationHelper';
    import { useLiveScore } from '@/core/oddsApi/composable/useLiveScore';
    import { SiteStyle } from '@/interface/enum';
    import type { ITournamentEvent } from '@/interface/tournamentCenter';
    import { useToggleStore } from '@/store/toggleStore';

    const defaultRedCardCountForDisplay = 1;

    const props = defineProps<{
        event: ITournamentEvent;
    }>();

    const { event } = toRefs(props);

    const { isSupported } = useLiveScoreRestriction(event);
    const { liveScore: providerLiveScore } = useLiveScore(event.value.id, isSupported);

    const { displayScore } = useLiveScoreDisplay(event, providerLiveScore, ref(null), SiteStyle.Asia, ref(true));

    const homeTeamImage = computed(() => getTournamentCenterTeamImage(event.value.homeTeam.countryName));
    const awayTeamImage = computed(() => getTournamentCenterTeamImage(event.value.awayTeam.countryName));
    const homePlayerImage = computed(() => getTournamentCenterPlayerImage(event.value.homeTeam.countryName));
    const awayPlayerImage = computed(() => getTournamentCenterPlayerImage(event.value.awayTeam.countryName));
    const { countdownDisplay, isShowCountdown: isShowCountdownTimer } = useEventCountdown(event);

    const { tournamentCenterMainLeagueIds } = storeToRefs(useToggleStore());

    const isShowCountdown = computed(() => isShowCountdownTimer.value && tournamentCenterMainLeagueIds.value.includes(event.value.league.id));

    const homeTeamRedCard = computed(() => event.value.homeTeam.redCard ?? 0);
    const awayTeamRedCard = computed(() => event.value.awayTeam.redCard ?? 0);
    const homeName = computed(() => getTranslated(event.value.homeTeam.name));
    const awayName = computed(() => getTranslated(event.value.awayTeam.name));

    const { isStatisticsAvailable, openStatistics } = useStatistics(event);

    const dateDisplay = computed(() => (formatDate(event.value.showTime, 'MM/dd')));
    const timeDisplay = computed(() => (formatDate(event.value.showTime, 'HH:mm')));

</script>

<style lang="scss" scoped>
@import "@/components/tournamentCenter/MatchInfo";
</style>
